import { api, time } from "@vuumly-common/common";
import { ChartData, ChartOptions, TooltipItem } from "chart.js";
import Color from "color";
import {
  useOperatorMode,
  useQuery,
  useTheme,
  useTimeGranularity,
  useTranslate
} from "hooks";
import _ from "lodash";
import { RoutePathEnum } from "../../../controllers/router";
import { BaseLineChart } from "../../Charts";
import { TimeGranularityTabs } from "../../Tabs/TimeGranularityTabs";
import { TextH5 } from "../../Typography/Text";
import { BaseCard } from "../Base.card";

interface Props {
  href?: RoutePathEnum;
  includeOriginId?: boolean;
  disableLink?: boolean;
  horizontal?: boolean;
  chartHeight?: number;
}

export const UtilizationTimeseriesLineChartCard = (props: Props) => {
  const { tabs, timeGranularity, changeGranularity, modifiedQueryParams } =
    useTimeGranularity();
  const theme = useTheme();
  const translate = useTranslate();
  const { operatorMode } = useOperatorMode();

  const { disableLink = true } = props;

  const { data, isLoading } = useQuery({
    apiEndpoint: api.Endpoint.Utilization,
    params: modifiedQueryParams,
  });

  const clonedData = _.cloneDeep(data) as ChartData<"line">;

  if (clonedData?.datasets) {
    clonedData.datasets = clonedData?.datasets.filter(
      (dataset) => dataset.label === "utilization_rate"
    );
  }

  clonedData?.datasets.forEach((dataset) => {
    const { label } = dataset;

    if (label === "utilization_rate") {
      dataset.label = translate("Overall utilization rate");
      dataset.parsing = {
        yAxisKey: "utilization_rate",
      };

      dataset.backgroundColor = operatorMode
        ? theme.otherOperatorLineColor
        : theme.accentColor;

      dataset.borderColor = dataset.backgroundColor;

      dataset.trendlineLinear = {
        colorMax: Color(dataset.backgroundColor).alpha(0.5).rgb().string(),
        colorMin: Color(dataset.backgroundColor).alpha(0.5).rgb().string(),
        lineStyle: "dashed",
        width: 2,
        projection: false,
      };
    }
  });

  const options: ChartOptions<"line"> = {
    parsing: {
      xAxisKey: "local_time",
    },

    scales: {
      x: {
        type: "timeseries",
      },
      y: {
        title: {
          text: "Utilization rate",
        },
        ticks: {
          callback: (value) => {
            return `${value}%`;
          },
        },
      },
    },

    plugins: {
      tooltip: {
        callbacks: {
          label: (item: TooltipItem<"line">) => {
            return `${item.dataset.label}: ${item.formattedValue}%`;
          },
        },
      },
    },
  };

  const Header = () => {
    return (
      <div className="flex justify-between w-full">
        <TextH5>{translate("Utilization over time")}</TextH5>
        <TimeGranularityTabs
          tabs={tabs}
          defaultValue={timeGranularity || time.GranularityEnum.Day}
          onChange={changeGranularity}
        />
      </div>
    );
  };

  return (
    <BaseCard
      isLoading={isLoading}
      header={<Header />}
      href={disableLink ? undefined : RoutePathEnum.Rides}
    >
      <BaseLineChart options={options} data={clonedData} />
    </BaseCard>
  );
};
